import React from 'react';
import { Platform, ScrollView, StyleSheet, View } from 'react-native';
import { noop, theme, VStack } from '@sweetgreen/garnish';

import { LoadingAnimation, TransparentModal } from '@order/components';
import { useCustomer } from '@order/Customer';

import { Footer, UsageHint } from '../ScanToPay.components';
import { useScanToPayFeatureFlag } from '../ScanToPay.featureFlag';
import { useScanToPayNavigation } from '../ScanToPay.navigation';
import { ScanToPayQrCode } from '../ScanToPay.qrcode';
import { CreditSelector } from '../Selectors/ScanToPay.credit';
import { GiftCodeSelector } from '../Selectors/ScanToPay.giftCode';
import { PaymentMethodSelector } from '../Selectors/ScanToPay.payments';

export const ScanToPayOptions = () => {
  const { goToPayments, goToGiftCode, goBack, handleSelectAddPaymentMethod } =
    useScanToPayNavigation();

  // ─── disable feature if missing qrcode or feature flag is off  ──────────
  const { customer, isCustomerLoaded } = useCustomer();
  const isScanToPayEnabled = useScanToPayFeatureFlag();
  const canCustomerScanToPay = Boolean(customer.qrCode);
  const shouldDisableScanToPay = !isScanToPayEnabled || !canCustomerScanToPay;
  const shouldRedirectBack = isCustomerLoaded && shouldDisableScanToPay;

  // ─── redirect to payments if customer has no billing accounts  ──────────
  const hasBillingAccounts = (customer.billingAccounts ?? []).length > 0;
  const shouldRedirectToPayments = isCustomerLoaded && !hasBillingAccounts;

  // ─── automatic redirect  ────────────────────────────────────────────────
  React.useLayoutEffect(() => {
    if (shouldRedirectBack) goBack();
    else if (shouldRedirectToPayments) handleSelectAddPaymentMethod();
  }, [
    shouldRedirectBack,
    shouldRedirectToPayments,
    goBack,
    handleSelectAddPaymentMethod,
  ]);

  if (shouldDisableScanToPay) return null;

  if (!customer?.qrCode) return <LoadingAnimation />;

  return (
    <TransparentModal size="full" onClose={noop} mobileEdges={['bottom']}>
      <View testID="scan-to-pay.options.route" style={styles.container}>
        <View style={styles.qrCodeContainer}>
          <ScanToPayQrCode value={customer.qrCode} />
        </View>

        <View style={styles.contentContainer}>
          <ScrollView contentContainerStyle={styles.scrollContainer}>
            <VStack
              customSpacing={0}
              style={styles.stackContainer}
              divider={true}
              dividerColor={theme.colors.LIGHT}
            >
              <UsageHint />
              <PaymentMethodSelector onSelect={goToPayments} />
              <CreditSelector />
              <GiftCodeSelector onSelect={goToGiftCode} />
            </VStack>
          </ScrollView>
        </View>

        <View style={styles.footerContainer}>
          <Footer />
        </View>
      </View>
    </TransparentModal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.KHAKI_3,

    // Because native platforms use varying spacing for the screen header area
    // (even when it is disabled), we must apply different top padding per platform.
    paddingTop: Platform.select({
      ios: 0,
      web: theme.spacing.xlarge,
      default: theme.spacing.medium,
    }),
  },
  qrCodeContainer: {
    paddingVertical: theme.spacing.medium,
  },
  contentContainer: {
    flex: 1,
    paddingTop: theme.spacing.medium,
  },
  footerContainer: {
    paddingTop: theme.spacing.medium,
  },
  scrollContainer: {
    flexGrow: 1,
  },
  stackContainer: {
    paddingHorizontal: theme.spacing.small,
  },
});
